import React, { useMemo, useState } from "react";
import { Button, Icon } from "semantic-ui-react";
import { useLanguage } from "../../../../contexts";
import { P, motor, useEnergia, useGuardarSeccion } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { useEvidencias } from "../common/useEvidencias";
import { Notice, SaveBar, SaveErrors, StatusBadge } from "../common/SectionCommon";
import "../common/common.scss";
import "../DocumentChecklist/DocumentChecklist.scss";
import "./SelfAssessmentForm.scss";

const tomar = (inst) => ({ _selfForm: { ...(inst._selfForm || {}) }, _selfNotes: { ...(inst._selfNotes || {}) } });
const fmt = (v, d = 0) => P.fmt(v, d);
const RESPUESTAS = [["si", "energy_performance_yes"], ["parcial", "energy_performance_partial"], ["no", "energy_performance_no"]];
const hoy = () => new Date().toISOString().slice(0, 10);

// Formulario de autoevaluación — madurez declarada de la gestión (hoja 07). Los cruces comparan lo declarado con los datos.
export function SelfAssessmentForm({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { archivo, inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });
  const evid = useEvidencias(guardar, t, installation?.site);
  const [archivos, setArchivos] = useState({});

  const vivo = useMemo(() => (archivo && b ? motor.calcularVista({ ...archivo, instalacion: { ...inst, ...b } }) : null), [archivo, inst, b]);
  const fv = (vivo && vivo.f) || f;

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;
  if (!fv) return <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>;

  const sf = P.sfCalc(fv);
  const nivel = sf.score == null ? null : P.SF_NIVELES.find(([min]) => sf.score >= min);
  const dims = [...new Set(sf.qs.map((q) => q.dim))];
  const evidencias = inst._selfEvid || {};

  const responder = (id, v) => {
    const r = { ...b._selfForm };
    if (r[id] === v) delete r[id]; else r[id] = v;
    cambiar("_selfForm", r);
  };
  const nota = (id, v) => cambiar("_selfNotes", { ...b._selfNotes, [id]: v });
  const subir = (id) => evid.subir(archivos[id], ({ nombre, archivo: a }) => ({
    _selfEvid: { ...evidencias, [id]: [...(evidencias[id] || []), { nombre, fecha: hoy(), archivo: a }] },
  })).then((ok) => { if (ok) setArchivos((x) => ({ ...x, [id]: null })); });
  const borrarEvidencia = (id, e) => evid.borrar(e.archivo, { _selfEvid: { ...evidencias, [id]: (evidencias[id] || []).filter((x) => x !== e) } });

  return (
    <div className="ep-screen ep-form">
      <h3 className="ep-screen__title">{t("energy_performance_form_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_form_intro")}</p>

      <div className="ep-kpis">
        <div className="ep-kpi">
          <div className="ep-kpi__label">{t("energy_performance_form_maturity")}</div>
          <div className="ep-kpi__value">{sf.score == null ? "—" : `${fmt(sf.score, 0)}%`}</div>
          <div className="ep-kpi__sub">{nivel ? <><strong>{nivel[1]}</strong> — {nivel[2]}</> : t("energy_performance_form_no_answers")}</div>
        </div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_form_answered")}</div><div className="ep-kpi__value">{sf.contestadas}/{sf.qs.length}</div></div>
      </div>
      {sf.cruces.sobre.length > 0 && (
        <Notice titulo={t("energy_performance_form_over")}>{sf.cruces.sobre.map((q) => `${q.cl} · ${q.q}`).join(" | ")}</Notice>
      )}
      {sf.cruces.sub.length > 0 && (
        <Notice tipo="info" titulo={t("energy_performance_form_under")}>{sf.cruces.sub.map((q) => `${q.cl} · ${q.q}`).join(" | ")}</Notice>
      )}
      {sf.cruces.adjunto.length > 0 && (
        <Notice tipo="info" titulo={t("energy_performance_form_attached")}>{sf.cruces.adjunto.map((q) => `${q.cl} · ${q.q}`).join(" | ")}</Notice>
      )}
      {dirty && <Notice tipo="info">{t("energy_performance_docs_save_first")}</Notice>}

      {dims.map((dim, k) => {
        const info = P.PARTE_INFO[dim] || { n: 5 + k, resp: t("energy_performance_form_process_owner"), desc: t("energy_performance_form_process_desc") };
        const d = sf.dims[dim];
        return (
          <section className="ep-section" key={dim}>
            <header className="ep-section__header">
              <h4 className="ep-section__title">{t("energy_performance_form_part")} {info.n} · {dim}</h4>
              <span className="ep-section__note">{d && d.n ? `${fmt((d.sum / d.n) * 100, 0)}% · ${d.n}/${d.tot}` : `0/${d ? d.tot : 0}`}</span>
            </header>
            <p className="ep-section__note"><strong>{info.resp}.</strong> {info.desc}</p>
            {sf.qs.filter((q) => q.dim === dim).map((q) => (
              <div className="ep-form__q" key={q.id}>
                <div className="ep-form__text"><span className="ep-mono">{q.cl}</span> {q.q}</div>
                <div className="ep-docs__seg" role="group" aria-label={q.q}>
                  {RESPUESTAS.map(([v, key]) => (
                    <button key={v} type="button" aria-pressed={b._selfForm[q.id] === v} className={b._selfForm[q.id] === v ? `on-${v}` : ""} onClick={() => responder(q.id, v)}>{t(key)}</button>
                  ))}
                </div>
                <input type="text" className="ep-form__note" aria-label={`${t("energy_performance_form_note")} ${q.id}`} placeholder={t("energy_performance_form_note")} value={b._selfNotes[q.id] || ""} onChange={(e) => nota(q.id, e.target.value)} />
                <div className="ep-form__evid">
                  {(evidencias[q.id] || []).map((e, j) => (
                    <span key={j} className="ep-docs__file">
                      ✓ {e.archivo ? <button type="button" className="ep-link" onClick={() => evid.ver(e.archivo)}>{e.nombre}</button> : e.nombre} ({e.fecha})
                      <button type="button" className="ep-docs__del" aria-label={`${t("energy_performance_delete")} ${e.nombre}`} disabled={dirty || evid.ocupado} onClick={() => borrarEvidencia(q.id, e)}>×</button>
                    </span>
                  ))}
                  <input type="file" aria-label={`${t("energy_performance_evidence_file")} ${q.id}`} disabled={dirty || evid.ocupado} onChange={(e) => setArchivos((x) => ({ ...x, [q.id]: e.target.files && e.target.files[0] }))} />
                  <Button type="button" size="mini" basic disabled={dirty || evid.ocupado || !archivos[q.id]} onClick={() => subir(q.id)}><Icon name="upload" /> {t("energy_performance_evidence_upload")}</Button>
                </div>
              </div>
            ))}
          </section>
        );
      })}

      <p className="ep-section__note"><StatusBadge estado="Pendiente">{t("energy_performance_notice")}</StatusBadge> {t("energy_performance_form_declared_note")}</p>
      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={0} saving={saving} onSave={() => guardar(b)} onDiscard={descartar} t={t} />
    </div>
  );
}
